import * as THREE from 'three';
import { TEX_SIZE, type TexKind, type TexLayer, type TexResult } from './textures.shared';

/**
 * Procedural textures. Heavy noise fields are generated in parallel workers
 * (one per texture) and uploaded as DataTextures with mipmaps; small gradient
 * sprites are drawn on 2D canvases on the main thread.
 */
const KINDS: TexKind[] = ['stone', 'floor', 'road', 'block', 'smoke'];
/** Bump when generator code changes, to invalidate the cache. */
const CACHE_VERSION = 2;

/**
 * Generate all textures on a pool of workers. Each texture is split into
 * horizontal bands so every core works on it; bands are stitched on arrival.
 */
async function generateAll(): Promise<Record<TexKind, TexResult>> {
  const n = Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1));
  const jobs: { kind: TexKind; y0: number; y1: number }[] = [];
  for (const kind of KINDS) {
    const h = TEX_SIZE[kind][1], bands = kind === 'smoke' ? 1 : n, step = Math.ceil(h / bands);
    for (let y0 = 0; y0 < h; y0 += step) jobs.push({ kind, y0, y1: Math.min(h, y0 + step) });
  }
  // biggest first for better packing
  jobs.sort((a, b) => TEX_SIZE[b.kind][0] * (b.y1 - b.y0) - TEX_SIZE[a.kind][0] * (a.y1 - a.y0));
  const out = {} as Record<TexKind, TexResult>;
  const put = (job: (typeof jobs)[number], part: TexResult) => {
    const [w, h] = TEX_SIZE[job.kind];
    const dst = (out[job.kind] ??= {});
    for (const [name, l] of Object.entries(part)) {
      const full = (dst[name] ??= { w, h, ch: l.ch, data: new Uint8ClampedArray(w * h * l.ch) });
      full.data.set(l.data, job.y0 * w * l.ch);
    }
  };
  await Promise.all(Array.from({ length: n }, async () => {
    const w = new Worker(new URL('./textures.worker.ts', import.meta.url), { type: 'module' });
    try {
      for (let job = jobs.shift(); job; job = jobs.shift()) {
        const j = job;
        const part = await new Promise<TexResult>((resolve, reject) => {
          w.onmessage = (e: MessageEvent<TexResult>) => resolve(e.data);
          w.onerror = reject;
          w.postMessage(j);
        });
        put(j, part);
      }
    } finally { w.terminate(); }
  }));
  return out;
}

/* ---------- IndexedDB cache: second load skips generation entirely ---------- */
function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open('orb-textures', 1);
    r.onupgradeneeded = () => r.result.createObjectStore('tex');
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function cacheGet(): Promise<Record<TexKind, TexResult> | null> {
  try {
    const db = await idb();
    return await new Promise((resolve) => {
      const q = db.transaction('tex').objectStore('tex').get(CACHE_VERSION);
      q.onsuccess = () => resolve(q.result ?? null);
      q.onerror = () => resolve(null);
    });
  } catch { return null; }
}
async function cachePut(v: Record<TexKind, TexResult>) {
  try {
    const db = await idb();
    const tx = db.transaction('tex', 'readwrite');
    tx.objectStore('tex').clear();
    tx.objectStore('tex').put(v, CACHE_VERSION);
  } catch { /* private mode etc. — just regenerate next time */ }
}

function data(l: TexLayer, srgb: boolean, repeat = true): THREE.DataTexture {
  const t = new THREE.DataTexture(new Uint8Array(l.data.buffer), l.w, l.h, l.ch === 4 ? THREE.RGBAFormat : THREE.RedFormat);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.anisotropy = 8;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.needsUpdate = true;
  return t;
}

const mk = (w: number, h: number) => {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  return [c, c.getContext('2d')!] as const;
};
function canvasTex(c: HTMLCanvasElement, srgb = true): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  return t;
}

function radial(stops: [number, string][], size = 128): HTMLCanvasElement {
  const [c, g] = mk(size, size), h = size / 2, r = g.createRadialGradient(h, h, 0, h, h, h);
  stops.forEach(([o, col]) => r.addColorStop(o, col));
  g.fillStyle = r; g.fillRect(0, 0, size, size);
  return c;
}

function shaft(): HTMLCanvasElement {
  const [c, g] = mk(64, 256), l = g.createLinearGradient(0, 0, 0, 256);
  l.addColorStop(0, 'rgba(255,255,255,1)'); l.addColorStop(0.35, 'rgba(255,255,255,.35)'); l.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = l; g.fillRect(0, 0, 64, 256);
  const h = g.createLinearGradient(0, 0, 64, 0);
  h.addColorStop(0, 'rgba(0,0,0,1)'); h.addColorStop(0.3, 'rgba(0,0,0,0)'); h.addColorStop(0.7, 'rgba(0,0,0,0)'); h.addColorStop(1, 'rgba(0,0,0,1)');
  g.globalCompositeOperation = 'destination-out'; g.fillStyle = h; g.fillRect(0, 0, 64, 256);
  return c;
}

function hot(): HTMLCanvasElement {
  const [c, g] = mk(128, 192), r = g.createRadialGradient(64, 180, 0, 64, 150, 170);
  r.addColorStop(0, '#fff6d8'); r.addColorStop(0.2, '#ffb45a'); r.addColorStop(0.48, '#e2501a'); r.addColorStop(0.85, '#3e0d05'); r.addColorStop(1, '#100302');
  g.fillStyle = r; g.fillRect(0, 0, 128, 192);
  return c;
}

export interface Textures {
  stone: { map: THREE.Texture; bump: THREE.Texture };
  block: { map: THREE.Texture; bump: THREE.Texture };
  /** map.a = lava-seam mask (emissive hue is fixed in the floor shader) */
  floor: { map: THREE.Texture; bump: THREE.Texture };
  road: { map: THREE.Texture; bump: THREE.Texture; emissive: THREE.Texture };
  smoke: THREE.Texture;
  glow: THREE.Texture;
  blob: THREE.Texture;
  shaft: THREE.Texture;
  hot: THREE.Texture;
}

export async function loadTextures(): Promise<Textures> {
  const t0 = performance.now();
  let all = await cacheGet();
  const cached = !!all;
  if (!all) { all = await generateAll(); void cachePut(all); }
  if (import.meta.env.DEV) console.info(`[textures] ${cached ? 'cache' : 'generated'} in ${(performance.now() - t0).toFixed(0)} ms`);
  const { stone, floor, road, block, smoke } = all;
  const sm = data(smoke.a, true);
  return {
    stone: { map: data(stone.c, true), bump: data(stone.b, false) },
    block: { map: data(block.c, true), bump: data(block.b, false) },
    floor: { map: data(floor.c, true, false), bump: data(floor.b, false, false) },
    road: { map: data(road.c, true, false), bump: data(road.b, false, false), emissive: data(road.e, true, false) },
    smoke: sm,
    glow: canvasTex(radial([[0, 'rgba(255,255,255,1)'], [0.2, 'rgba(255,255,255,.5)'], [0.5, 'rgba(255,255,255,.1)'], [1, 'rgba(255,255,255,0)']])),
    blob: canvasTex(radial([[0, 'rgba(0,0,0,1)'], [0.45, 'rgba(0,0,0,.7)'], [1, 'rgba(0,0,0,0)']])),
    shaft: canvasTex(shaft()),
    hot: canvasTex(hot()),
  };
}
